'use client';

import { PatternCTASet, PatternTitleSubtitle, StaticImage } from 'mmds';
import { BillboardBlockItem, BillboardProps } from './types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import proxyImageUrlIfEnabled from '@app/lib/images/proxy-image-url-if-enabled';
import cn from 'clsx';
import s from './Billboard.module.css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css';
import { generateTheme } from '@app/lib/helpers/generate-theme';

const BillboardCarouselVariant = ({ block, context }: BillboardProps) => {
    const { items } = block;
    return (
        <>
            <Swiper
                pagination={{
                    type: 'bullets',
                    clickable: true,
                }}
                effect={'fade'}
                speed={1000}
                fadeEffect={{
                    crossFade: true,
                }}
                autoplay={{
                    delay: 5000,
                    disableOnInteraction: false,
                }}
                navigation={true}
                modules={[Autoplay, Pagination, Navigation, EffectFade]}
                className="mobile_billboard_swiper swiper_billboard"
            >
                <div className={s.root}>
                    {items.map((item: BillboardBlockItem, index: number) => {
                        const {
                            desktopImage,
                            mobileImage,
                            contentPosition,
                            title,
                            subTitle,
                            ctaSet,
                            titleSize,
                            subTitleSize,
                            titleTag,
                            mobileTheme,
                            desktopTheme,
                        } = item;

                        const theme = generateTheme({
                            mobileTheme,
                            desktopTheme,
                        });

                        const contentPositionWithDefault =
                            contentPosition || 'bottomLeft';

                        const contentLayoutClassname = cn({
                            [s.center]: contentPosition === 'center',
                            [s.bottomLeft]:
                                contentPositionWithDefault === 'bottomLeft',
                            [s.topLeft]: contentPosition === 'topLeft',
                        });
                        const buttonContentLayout = cn({
                            [s.buttonCenter]: contentPosition === 'center',
                            [s.buttonBottomLeft]:
                                contentPositionWithDefault === 'bottomLeft',
                            [s.buttonTopLeft]: contentPosition === 'topLeft',
                        });
                        const identifier = `bb_carousel_${index}`;

                        return (
                            <SwiperSlide key={index} data-testid={identifier}>
                                <div
                                    className={s.billboardRoot}
                                    key={identifier}
                                    data-testid={identifier}
                                    data-theme={theme}
                                >
                                    <StaticImage
                                        desktopImage={desktopImage}
                                        mobileImage={mobileImage}
                                        altDescription={title}
                                        fullWidth={true}
                                        urlTransformer={proxyImageUrlIfEnabled}
                                    />
                                    <div
                                        className={`${s.overlayContent} ${contentLayoutClassname}`}
                                    >
                                        <div
                                            className={cn(
                                                s.content,
                                                contentLayoutClassname,
                                                contentPosition !== 'center'
                                                    ? s.setWidthWithFlex
                                                    : s.setWidth
                                            )}
                                        >
                                            <div className={s.description}>
                                                <PatternTitleSubtitle
                                                    title={title}
                                                    titleSize={titleSize}
                                                    titleTag={titleTag}
                                                    subTitle={subTitle}
                                                    subTitleSize={subTitleSize}
                                                    billboardType="carousel"
                                                />
                                            </div>
                                            {ctaSet && (
                                                <div
                                                    className={
                                                        buttonContentLayout
                                                    }
                                                >
                                                    <PatternCTASet
                                                        set={ctaSet}
                                                        lang={context.lang}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </SwiperSlide>
                        );
                    })}
                </div>
            </Swiper>
            <style jsx global>
                {`
                    .mobile_billboard_swiper .swiper_billboard {
                        width: 100%;
                        height: 100%;
                        z-index: 0;
                        background-color: rgb(224, 228, 227);
                    }

                    .swiper-slide .desktopImage {
                        display: block;
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }

                    .swiper-pagination-bullet-active {
                        background: #ffffff !important;
                    }

                    .swiper-pagination-bullet {
                        background: unset;
                        opacity: unset;
                    }

                    .swiper-pagination-clickable .swiper-pagination-bullet {
                        cursor: pointer;
                        border: 1px solid #ffffff;
                    }

                    .mobile_billboard_swiper .swiper-button-next:after,
                    .mobile_billboard_swiper .swiper-button-prev:after {
                        display: none;
                    }

                    .swiper-pagination-fraction,
                    .swiper-pagination-custom,
                    .swiper-horizontal > .swiper-pagination-bullets,
                    .swiper-pagination-bullets.swiper-pagination-horizontal {
                        bottom: var(--swiper-pagination-bottom, 20px);
                    }

                    @media screen and (max-width: 767px) {
                        .swiper-pagination-fraction,
                        .swiper-pagination-custom,
                        .swiper-horizontal > .swiper-pagination-bullets,
                        .swiper-pagination-bullets.swiper-pagination-horizontal {
                            bottom: var(--swiper-pagination-bottom, 10px);
                        }
                    }
                `}
            </style>
        </>
    );
};

export default BillboardCarouselVariant;
